import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, cartApi } from '../lib/api';
import { toast } from 'sonner';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchCartCount = useCallback(async () => {
    try {
      const response = await cartApi.get();
      setCartCount(response.data.items?.length || 0);
    } catch (error) {
      console.error('Failed to fetch cart count:', error);
    }
  }, []);

  const login = (userData, token) => {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    if (!userData.is_admin) {
      fetchCartCount();
    }
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setUser(null);
    setCartCount(0);
    toast.success('Logged out successfully');
  };

  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };

    window.addEventListener('unauthorized', handleUnauthorized);

    const initApp = async () => {
      const token = sessionStorage.getItem('token');
      const userData = sessionStorage.getItem('user');
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          if (!parsedUser.is_admin) {
            await fetchCartCount();
          }
        } catch (error) {
          console.error('Auth initialization failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initApp();

    return () => {
      window.removeEventListener('unauthorized', handleUnauthorized);
    };
  }, [fetchCartCount]);

  const value = {
    user,
    setUser,
    login,
    logout,
    cartCount,
    setCartCount,
    fetchCartCount,
    loading
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
